import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { PolicyChangeService } from 'gw-capability-gateway';

import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLDelinquentsReportComponent.messages';
import metadata from './MLDelinquentsReportComponent.metadata.json5';

const MLDelinquentsReportComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        getCustomerDisplayNameRevert,
        history,
        convertLocalDateToDate
    } = props;
    const { gatewayParamConfig } = appConfig;
    const translator = useTranslator();

    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const loadCancellationsData = useCallback(async (request, numOfDays) => {
        updateIsLoading(true);

        const response = isDashboard
            ? await PolicyChangeService.getCancelJobsForCurrentUser(gatewayParamConfig.jobsCancelledInLastXDays, rowLimit, request, authHeader)
            : await PolicyChangeService.getCancelJobsForCurrentUser(numOfDays, null, request, authHeader);

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, gatewayParamConfig.jobsCancelledInLastXDays, isDashboard, rowLimit]);

    useEffect(() => {
        loadCancellationsData(null, gatewayParamConfig.jobsCancelledInLastXDays);

        const setFilterDate = (date) => {
            const currentDate = new Date();
            if (date < currentDate) {
                filterData.fromDate_ML = {
                    year: date.getFullYear(),
                    month: date.getMonth(),
                    day: date.getDate()
                };
            } else {
                filterData.toDate_ML = {
                    year: date.getFullYear(),
                    month: date.getMonth(),
                    day: date.getDate()
                };
            }
        };
        
        const prefillDateRange = () => {
            const priorDate = new Date();
            const futureDate = new Date();

            priorDate.setDate(priorDate.getDate() - gatewayParamConfig.jobsCancelledInLastXDays);
            futureDate.setDate(futureDate.getDate() + gatewayParamConfig.jobsCancelledInLastXDays);

            setFilterDate(priorDate);
            setFilterDate(futureDate);
        };        

        prefillDateRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFilter = () => {
        loadCancellationsData(filterData, 0);
    };

    const onClearFilters = () => {
        updateFilterData({});
    };

    const renderNotificationDateCell = (item) => {
        return item.notificationDate_ML ? moment(item.notificationDate_ML).format('MM/DD/YYYY') : '-';
    };

    const isDateInPast = (date)=> {
        return moment(date).isBefore(moment(), 'd');
    };

    const renderCancelDateCell = (item) => {
        let cancelDateClass = '';
        if (isDateInPast(item.cancelDate_ML)) {
            cancelDateClass = 'error-text';
        }
        return item.cancelDate_ML ? <span className={cancelDateClass}>{convertLocalDateToDate(item.cancelDate_ML)}</span> : '-';
    };

    const renderCustomerName = (item) => {
        if(_.isUndefined(item.displayName_ML)) {
            return '-'
        }
        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {_.capitalize(getCustomerDisplayNameRevert(item.accountHolder_ML))}
            </Link>
        )
    };

    const renderProductCell = (item) => {
        return item.product.productName ? item.product.productName : '-';
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderDescriptionCell = (item) => {
        if (item.cancelReason_ML) {
            return item.cancelReason_ML;
        }
        return '-';
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'delinquents-report';
        history.push('/activity-reports', {reportView : reportView} );
    }

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        delinquentsReportFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            dateLabel: translator(messages.cancelDateRange)
        },
        delinquentsReportTable: {
            data: _.orderBy(filteredJobs, 'notificationDate_ML', 'asc'),
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noRecentBillingActivityFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: translator(messages.cancellations)
        },
        viewAllBillingsAndCancelButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_billing"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderNotificationDateCell,
            renderCancelDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderDescriptionCell
        },
        resolveClassNameMap: styles
    };
    
    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLDelinquentsReportComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired
}

export default withRouter(MLDelinquentsReportComponent);
